import moment from 'moment';
import React from 'react';
import Button from '../../components/Button';
import CallOut from '../../components/CallOut';
import { Cards } from '../../components/Card';
import MetaList, { MetaListItem } from '../../components/MetaList';
import { gql } from '../../graphql/generated/gql';
import type { DocumentType } from '../../graphql/generated/gql';
import type { PlanInterval, PlanTier } from '../../graphql/generated/graphql';
import useSeatLimits from '../../invitation/useSeatLimits';
import SettingCard from '../../settings/SettingCard';
import SettingsSection from '../../settings/SettingsSection';
import formatCurrency from '../../utils/formatCurrency';
import Link from '../../utils/router/Link';
import AdminBillingNotice from './AdminBillingNotice';
import BillingEmailCard from './BillingEmailCard';
import * as styles from './BillingStatusView.module.scss';
import PaymentCard from './PaymentCard';
import Plan from './Plan';

const STRIPE_PORTAL_URL = 'https://billing.slab.com/p/login/aEUg0r76C99G2kw000';

const BILLING_SUMMARY_FRAGMENT = gql(`
  fragment BillingStatusView_billingSummary on BillingSummary {
    # eslint-disable-next-line @graphql-eslint/require-selections
    coupon {
      name
    }
    # eslint-disable-next-line @graphql-eslint/require-selections
    customer {
      balance
    }
    periodEndAt
    # eslint-disable-next-line @graphql-eslint/require-selections
    ...PaymentCard_billingSummary
    # eslint-disable-next-line @graphql-eslint/require-selections
    ...AdminBillingNotice_billingSummary
    # eslint-disable-next-line @graphql-eslint/require-selections
    ...Plan_billingSummary
    # eslint-disable-next-line @graphql-eslint/require-selections
    ...BillingEmailCard_billingSummary
  }
`);

const BILLING_PRICING_FRAGMENT = gql(`
  fragment BillingStatusView_billingPricing on BillingPricing {
    ...Plan_billingPricing
  }
`);

interface BillingStatusViewProps {
  billingSummary: DocumentType<typeof BILLING_SUMMARY_FRAGMENT>;
  billingPricing: DocumentType<typeof BILLING_PRICING_FRAGMENT>;
  updateBilling: (values: {
    email?: string;
    source?: string;
    planTier?: PlanTier;
    planInterval?: PlanInterval;
  }) => Promise<unknown>;
  cancelBilling: () => void;
  initialTier: PlanTier | null;
  initialInterval: PlanInterval | null;
}

const BillingStatusView = ({
  billingSummary,
  billingPricing,
  updateBilling,
  cancelBilling,
  initialTier,
  initialInterval,
}: BillingStatusViewProps) => {
  const { coupon, customer, periodEndAt } = billingSummary;
  const { balance } = customer;

  const { isSeatLimitMaxed } = useSeatLimits();

  const credit = balance && balance < 0 ? -balance : null;

  return (
    <>
      {isSeatLimitMaxed && (
        <div className={styles.limitCalloutContainer}>
          <CallOut variant="danger">
            Your team has reached the seat limit for your plan. Please{' '}
            <Link to="mailto:support@slab.com">contact support</Link> to add
            additional users.
          </CallOut>
        </div>
      )}
      <SettingsSection header="Plan details">
        <div className={styles.noticeWrapper}>
          <AdminBillingNotice
            billingSummary={billingSummary}
            isTrialEnded={false}
          />
        </div>
        <Plan
          billingSummary={billingSummary}
          billingPricing={billingPricing}
          updateBilling={updateBilling}
          initialTier={initialTier}
          initialInterval={initialInterval}
        />
      </SettingsSection>

      <SettingsSection header="Payment">
        <Cards>
          <BillingEmailCard
            billingSummary={billingSummary}
            updateBilling={updateBilling}
          />
          {!!credit && (
            <SettingCard
              title="Credits"
              details={`${formatCurrency(credit)} remaining in credit`}
            />
          )}
          <PaymentCard
            billingSummary={billingSummary}
            updateBilling={updateBilling}
          />
          {coupon && (
            <SettingCard
              title="Coupon"
              details={
                <MetaList>
                  <MetaListItem>{coupon.name}</MetaListItem>
                </MetaList>
              }
            />
          )}
          <SettingCard
            actions={
              <Button variant="secondary" to={STRIPE_PORTAL_URL} newTab>
                View
              </Button>
            }
            title="Invoices details and history"
            details="Review your past invoices or update the billing details they display"
          />
        </Cards>
      </SettingsSection>

      <SettingsSection header="Danger zone">
        <Cards>
          <SettingCard
            actions={
              <Button variant="secondary" danger onClick={cancelBilling}>
                Cancel subscription
              </Button>
            }
            title="Cancel subscription"
            details={`It will remain active until ${moment(periodEndAt).format(
              'LL',
            )}`}
          />
        </Cards>
      </SettingsSection>
    </>
  );
};

export default BillingStatusView;
